import { useContext, useEffect, useMemo, useState } from "react";
import {
  Search,
  Plus,
  RefreshCcw,
  Eye,
  Pencil,
  Barcode,
  Image as ImageIcon,
  Copy,
} from "lucide-react";
import BarcodeModals from "./BarcodeModals";
import InventoryIngredientsModal from "./InventoryIngredientsModal";
import InventoryViewModal from "./InventoryViewModal";
import useInventory from "../../../services/Inventory/useInventory";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { GlobalContext } from "../../../Context/GlobalContext";
import useRack from "../../../services/Inventory/useRack";
import ItemViewModal from "./ItemViewModal";
import EditItemModal from "./EditItemModal";
import NoIndex from "../../../components/NoIndex";
import LoadingScreen from "../../../utils/LoadingScreen";

export default function InventoryManagement() {
  const {
    items,
    loading,
    hasNextPage,
    inventoryPage,
    fetchInventory,
    addBarcodeToItem,
    updateInventoryItem,
    uploadItemImage,
    getSubCategories,
    updateCatalogItem,
    getRecipeIngredients,
    updateAllBarcodes,
  } = useInventory();

  const { rows: rackList, fetchRacks } = useRack();
  const { userID } = useContext(GlobalContext);
  const [statusFilter, setStatusFilter] = useState("");
  const [dishFilter, setDishFilter] = useState("");
  const [q, setQ] = useState("");
  const [barcodeSearch, setBarcodeSearch] = useState("");
  const [barcodeModalOpen, setBarcodeModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [barcodeValue, setBarcodeValue] = useState("");
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailItem, setDetailItem] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [imageFile, setImageFile] = useState(null);
  const [viewModalItem, setViewModalItem] = useState(null);
  const [viewModalTab, setViewModalTab] = useState("details");
  const [editableDetails, setEditableDetails] = useState({});
  const [availability, setAvailability] = useState("");
  const [selectedPrinter, setSelectedPrinter] = useState("");
  const [printerList, setPrinterList] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [bulkBarcodeModal, setBulkBarcodeModal] = useState(false);
  const [previewImage, setPreviewImage] = useState(viewModalItem?.photo || "");
  const [ingredients, setIngredients] = useState([
    { ingredient: "", quantity: "", unit: "" },
  ]);
  const [ingredientsModalOpen, setIngredientsModalOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [page, setPage] = useState(1);

  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchInventory(q, page);
    }, 500);

    return () => clearTimeout(timer);
  }, [q, page]);

  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const statusBadge = (s) =>
    s === "Y" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-600";

  const getDishLabel = (item) => {
    let icon = "";

    try {
      if (typeof item.icon_dish === "string") {
        const parsed = JSON.parse(item.icon_dish);

        if (Array.isArray(parsed)) {
          icon = parsed[0] || "";
        }
      } else if (Array.isArray(item.icon_dish)) {
        icon = item.icon_dish[0] || "";
      }
    } catch {
      icon = item.icon_dish || "";
    }

    icon = String(icon).toLowerCase();

    if (icon.includes("nonveg") || icon.includes("non-veg")) {
      return { label: "Non-Veg", color: "bg-red-500", type: "non-veg" };
    }

    if (icon.includes("egg")) {
      return { label: "Egg", color: "bg-yellow-500", type: "egg" };
    }

    if (icon.includes("veg")) {
      return { label: "Veg", color: "bg-green-500", type: "veg" };
    }

    return null;
  };

  const filtered = useMemo(() => {
    return items.filter((it) => {
      // BARCODE SEARCH
      const barcodeMatch =
        !barcodeSearch ||
        it.barcodeNumber?.toLowerCase().includes(barcodeSearch.toLowerCase()) ||
        it.item_id?.toString().includes(barcodeSearch);

      const status = it.inStock || it.status;

      const statusMatch =
        !statusFilter ||
        (statusFilter === "active" && status === "Y") ||
        (statusFilter === "inactive" && status !== "Y");

      const dish = getDishLabel(it);

      const dishMatch = !dishFilter || (dish && dish.type === dishFilter);

      return barcodeMatch && statusMatch && dishMatch;
    });
  }, [items, barcodeSearch, statusFilter, dishFilter]);

  const handleBarcodeSearch = () => {
    if (!barcodeSearch) {
      toast.error("Enter Barcode or Item ID");
      return;
    }

    const item = items.find(
      (it) =>
        it.barcodeNumber?.toLowerCase() === barcodeSearch.toLowerCase() ||
        it.item_id?.toString() === barcodeSearch,
    );

    if (!item) {
      toast.error("Item not found");
      return;
    }

    setViewModalItem(item);
    setViewModalTab("details");
    setBarcodeSearch("");
  };

  const handleAvailabilityChange = (value) => {
    setAvailability(value);
  };

  const handleCatalogImageChange = (e) => {
    const file = e.target.files[0];

    if (!file) return;

    setImageFile(file);

    const preview = URL.createObjectURL(file);

    setPreviewImage(preview);
  };

  const addIngredientRow = () => {
    setIngredients((prev) => [
      ...prev,
      { ingredient: "", quantity: "", unit: "" },
    ]);
  };

  const removeIngredientRow = (index) => {
    const updated = [...ingredients];

    updated.splice(index, 1);

    setIngredients(updated);
  };

  const handleIngredientChange = (index, field, value) => {
    const updated = [...ingredients];

    updated[index][field] = value;

    setIngredients(updated);
  };

  const copyBarcode = async (code) => {
    if (!code) return;

    try {
      await navigator.clipboard.writeText(code);
      toast.success("Barcode copied");
    } catch (err) {
      toast.error("Copy failed");
    }
  };

  const handleSavePrinter = () => {
    if (!selectedPrinter) {
      toast.error("Select printer");
      return;
    }

    toast.success("Printer assigned");
  };

  const handleAddPrinter = () => {
    navigate("/orders/ordersettings");
  };

  const handleDetailChange = (e) => {
    const { name, value } = e.target;

    setEditableDetails((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCreateBarcode = async () => {
    try {
      const res = await updateAllBarcodes();

      if (res?.responseCode === "00") {
        toast.success("Barcodes created successfully");

        await fetchInventory(q, page); // refresh inventory
      } else {
        toast.error(res?.responseMessage || "Failed to create barcodes");
      }
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong");
    } finally {
      setBulkBarcodeModal(false); // close modal
    }
  };

  const handleBarcodeSubmit = async () => {
    if (!barcodeValue) {
      toast.error("Enter barcode");
      return;
    }

    const res = await addBarcodeToItem({
      barCode: barcodeValue,
      inventoryId: selectedItem.item_id,
    });

    if (res?.responseCode === "00") {
      toast.success("Barcode updated");

      setBarcodeModalOpen(false);
      setSelectedItem(null);

      fetchInventory(q, page);
    } else {
      toast.error(res?.responseMessage);
    }
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;

    setEditForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSaveAvailability = () => {
    if (!availability) {
      toast.error("Select availability");
      return;
    }

    toast.success("Availability saved");
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];

    if (!file) return;

    setImageFile(file);

    // preview only
    const preview = URL.createObjectURL(file);

    setEditForm((prev) => ({
      ...prev,
      photo: preview,
    }));
  };

  const handleUpdateItem = async () => {
    try {
      let imageUrl = editForm.photo;

      // IMAGE SELECTED
      if (imageFile) {
        const base64 = await fileToBase64(imageFile);

        const uploadRes = await uploadItemImage({
          userid: userID,
          item_name: editForm.item_name,
          type: "IMG",
          image_data: base64,
        });

        if (uploadRes?.status === "0") {
          imageUrl = uploadRes.image_url;
        }
      }

      const payload = {
        id: editItem.id,
        usCode: userID,
        itemName: editForm.item_name || editItem.item_name,
        status: editForm.inStock || editItem.status,
        itemDesc: editForm.item_description || editItem.item_description,
        rating: editForm.ratings || editItem.ratings,
        price: editForm.price || editItem.price,
        discount: editForm.discount || editItem.discount,
        discount_price: editForm.discount_price || editItem.discount_price,
        size: editForm.size || editItem.size,
        size_trans: editForm.size_trans || editItem.size_trans,
        photo: imageUrl || editItem.photo,
        barcodeNumber: editForm.barcodeNumber || editItem.barcodeNumber,
        inStock: editForm.inStock || editItem.inStock,
        stock: editForm.stock || editItem.stock,
        home_delivery: editForm.home_delivery || editItem.home_delivery,
        takeaway: editForm.takeaway || editItem.takeaway,
        dining: editForm.dining || editItem.dining,
        item_category: editForm.item_category || editItem.item_category,
        item_name_trans: editForm.item_name_trans || editItem.item_name_trans,
        item_desc_trans:
          editForm.item_description_trans || editItem.item_description_trans,
        icon_dish: editForm.icon_dish || editItem.icon_dish,
      };

      const res = await updateInventoryItem(payload);

      if (res?.responseCode === "00") {
        toast.success("Item updated");

        setEditModalOpen(false);

        fetchInventory(q, page);
      }
    } catch (err) {
      console.error(err);
      // toast.error("Update failed");
    }
  };

  const handleSaveCatalog = async () => {
    let imageUrl = viewModalItem.photo;

    // IMAGE UPLOAD
    if (imageFile) {
      const base64 = await fileToBase64(imageFile);

      const uploadRes = await uploadItemImage({
        item_name: editableDetails.item_name,
        image_data: base64,
      });

      if (uploadRes?.status === "0") {
        imageUrl = uploadRes.image_url;
      }
    }

    const payload = {
      userid: userID,

      id: viewModalItem.item_id,

      itemId: viewModalItem.item_id,

      itemName: editableDetails.item_name || "",

      desc: editableDetails.item_description || "",

      size: editableDetails.size || "",

      sizeId: editableDetails.sizeId || "",

      category: "",

      categoryId: editableDetails.sub_category || "",

      rackNo: editableDetails.rackNumber || "",

      expiryDate: editableDetails.expiryDate || "",

      sellingPrice: editableDetails.sellingPrice || "",

      homeDeliveryPrice: editableDetails.homeDeliveryPrice || "",

      takewayPrice: editableDetails.takewayPrice || "",

      diningPrice: editableDetails.diningPrice || "",

      roomOrder: editableDetails.roomOrder || "",

      carOrder: editableDetails.carOrder || "",

      taxPercentage: editableDetails.taxPercentage || "",

      variantId: editableDetails.variantId || "",

      variantName: editableDetails.variantName || "",

      image: imageUrl,
    };

    const res = await updateCatalogItem(payload);

    if (res?.responseCode === "00") {
      toast.success("Item updated");

      setEditModalOpen(false);

      fetchInventory(q, page);
    } else {
      toast.error(res?.responseMessage);
    }
  };

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      await fetchInventory(q, page);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (editItem) {
      setEditForm({
        item_name: editItem.item_name || "",
        item_description: editItem.item_description || "",
        price: editItem.price || "",
        discount: editItem.discount || "",
        discount_price: editItem.discount_price || "",
        ratings: editItem.ratings || "",
        photo: editItem.photo || "",
        barcodeNumber: editItem.barcodeNumber || "",
        size: editItem.size || "",
        stock: editItem.stock || "",
        item_category: editItem.item_category || "",
        inStock: editItem.inStock || "Y",
        home_delivery: editItem.home_delivery || "Y",
        takeaway: editItem.takeaway || "Y",
        dining: editItem.dining || "Y",
        item_name_trans: editItem.item_name_trans || "",
        item_description_trans: editItem.item_description_trans || "",
        size_trans: editItem.size_trans || "",
        icon_dish: editItem.icon_dish || "",
      });
    }
  }, [editItem]);

  useEffect(() => {
    if (viewModalItem) {
      setEditableDetails(viewModalItem);
    }
  }, [viewModalItem]);

  return (
    <div className="p-6 min-h-0 h-full flex flex-col overflow-hidden">
      {/* HEADER */}
      <NoIndex />
<ToastContainer/>

      <div className="flex items-center justify-between mb-5 montserrat-font-1">
        <h2 className="text-xl font-semibold text-gray-800">
          Inventory Management
        </h2>

        <div className="flex items-center gap-3 montserrat-font-1">
          {/* BARCODE UPDATE */}

          <button
            onClick={() => setBulkBarcodeModal(true)}
            className="flex items-center gap-2 bg-white border px-4 py-2 rounded-lg text-sm"
          >
            <Barcode size={16} />
            Update Item Barcode
          </button>

          {/* BARCODE SEARCH */}

          <div className="flex items-center bg-white border rounded-lg px-3 py-2 w-[260px] montserrat-font-1">
            <Search size={16} className="text-gray-400" />
            <input
              className="ml-2 outline-none text-sm w-full"
              placeholder="enter Barcode or Item ID"
              value={barcodeSearch}
              onChange={(e) => setBarcodeSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleBarcodeSearch();
                }
              }}
            />
          </div>

          {/* GLOBAL SEARCH */}

          <div className="flex items-center bg-white border rounded-lg px-3 py-2 w-[260px] montserrat-font-1">
            <Search size={16} className="text-gray-400" />
            <input
              className="ml-2 outline-none text-sm w-full"
              placeholder="Search name / item_id / category"
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setPage(1);
              }}
            />
          </div>

          {/* STATUS FILTER */}

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border bg-white rounded-lg px-3 py-2 text-sm"
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>

          {/* DISH FILTER */}

          <select
            value={dishFilter}
            onChange={(e) => setDishFilter(e.target.value)}
            className="border bg-white rounded-lg px-3 py-2 text-sm"
          >
            <option value="">All Dish</option>
            <option value="veg">Veg</option>
            <option value="non-veg">Non-Veg</option>
            <option value="egg">Egg</option>
          </select>

          {/* ADD ITEM */}

          <button
            onClick={() => navigate("/Orders/Catalogue/add-items")}
            className="flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm"
          >
            <Plus size={16} />
            Add Item
          </button>

          {/* REFRESH */}

          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="px-3 py-1.5 border bg-blue-500 flex items-center gap-2 text-white rounded-lg disabled:opacity-60"
          >
            <RefreshCcw
              size={16}
              className={refreshing ? "animate-spin" : ""}
            />
            Refresh
          </button>
        </div>
      </div>

      {/* TABLE */}

      <div className="bg-white rounded-xl border relative overflow-auto h-full montserrat-font-1">
        {/* HEADER */}

        <div className="sticky top-0 z-10 grid grid-cols-7 px-5 py-3 text-sm font-semibold text-gray-900 bg-green-100 border-b ">
          <div>Image</div>
          <div>Name</div>
          <div>Status</div>
          <div>Price</div>
          <div>Icon Dish</div>
          <div>Category</div>
          <div className="text-right">Actions</div>
        </div>

        {/* ROWS */}

        {refreshing && !loading && (
          <div className="absolute inset-0 bg-white/70 backdrop-blur-[1px] flex items-center justify-center z-10">
            <LoadingScreen text="Refreshing inventory..." />
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <LoadingScreen text="Loading inventory..." />
          </div>
        ) : (
          filtered.map((it, index) => {
            const dish = getDishLabel(it);

            return (
              <div
                key={`${it.item_id}-${index}`}
                className="grid grid-cols-7 px-5 py-4 items-center border-b hover:bg-gray-50"
              >
                {/* IMAGE */}

                <div>
                  {it.photo ? (
                    <img
                      src={it.photo}
                      className="w-12 h-12 rounded-lg object-cover"
                    />
                  ) : (
                    <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                      <ImageIcon size={16} />
                    </div>
                  )}
                </div>

                {/* NAME */}

                <div>
                  <div className="font-medium text-gray-800">
                    {it.item_name}
                  </div>

                  <div className="text-xs text-gray-500">
                    item_id: {it.item_id} | Stock: {it.stock}
                  </div>

                  <div className="text-xs text-gray-400 flex items-center gap-2">
                    <span>Barcode: {it.barcodeNumber || "—"}</span>

                    {it.barcodeNumber && (
                      <Copy
                        size={14}
                        className="cursor-pointer text-gray-500 hover:text-black"
                        onClick={() => copyBarcode(it.barcodeNumber)}
                        title="Copy Barcode"
                      />
                    )}

                    <span
                      className="text-blue-600 ml-2 cursor-pointer"
                      onClick={() => {
                        setSelectedItem(it);
                        setBarcodeValue(it.barcodeNumber || "");
                        setBarcodeModalOpen(true);
                      }}
                    >
                      Update
                    </span>
                  </div>
                </div>

                {/* STATUS */}

                <div>
                  <span
                    className={`px-3 py-1 text-xs rounded-full ${statusBadge(
                      it.inStock || it.status,
                    )}`}
                  >
                    {(it.inStock || it.status) === "Y" ? "Active" : "Inactive"}
                  </span>
                </div>

                {/* PRICE */}

                <div className="font-medium">₹{it.price || 0}</div>

                {/* ICON DISH */}

                <div className="flex justify-start">
                  {dish ? (
                    <span className="flex items-center gap-2 border rounded-full px-3 py-1 text-xs bg-gray-100 w-fit">
                      <span
                        className={`w-2.5 h-2.5 rounded-sm border ${dish.color}`}
                      ></span>

                      {dish.label}
                    </span>
                  ) : (
                    <span className="text-gray-400 text-xs">—</span>
                  )}
                </div>

                {/* CATEGORY */}

                <div>{it.category_name}</div>

                {/* ACTIONS */}

                <div className="flex justify-end gap-2">
                  <button
                    className="p-2 border rounded-lg hover:bg-gray-50"
                    onClick={() => {
                      setDetailItem(it);
                      setDetailModalOpen(true);
                    }}
                  >
                    <Eye size={16} />
                  </button>

                  <button
                    className="p-2 border rounded-lg hover:bg-blue-50 text-blue-600"
                    onClick={() => {
                      setEditItem(it);
                      setEditModalOpen(true);
                    }}
                  >
                    <Pencil size={16} />
                  </button>

                  <button
                    className="p-2 border rounded-lg hover:bg-gray-50"
                    onClick={() => {
                      setSelectedItem(it);
                      setBarcodeValue(it.barcodeNumber || "");
                      setBarcodeModalOpen(true);
                    }}
                  >
                    <Barcode size={16} />
                  </button>
                </div>
              </div>
            );
          })
        )}

        {!loading && (
          <div className="flex items-center justify-between px-5 py-4 border-t bg-white">
            <p className="text-sm text-gray-500">
              Page {page} • Showing {filtered.length} items
            </p>

            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={page === 1 || loading || refreshing}
                onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                className="px-4 py-2 border rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Previous
              </button>

              <button
                type="button"
                disabled={!hasNextPage || loading || refreshing}
                onClick={() => setPage((prev) => prev + 1)}
                className="px-4 py-2 border rounded-lg text-sm bg-blue-500 text-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* update Barcode modal */}
      <BarcodeModals
        barcodeModalOpen={barcodeModalOpen}
        setBarcodeModalOpen={setBarcodeModalOpen}
        selectedItem={selectedItem}
        barcodeValue={barcodeValue}
        setBarcodeValue={setBarcodeValue}
        handleBarcodeSubmit={handleBarcodeSubmit}
        bulkBarcodeModal={bulkBarcodeModal}
        setBulkBarcodeModal={setBulkBarcodeModal}
        handleCreateBarcode={handleCreateBarcode}
      />

      {/* Ingredients Modal */}
      <InventoryIngredientsModal
        ingredientsModalOpen={ingredientsModalOpen}
        setIngredientsModalOpen={setIngredientsModalOpen}
        ingredients={ingredients}
        viewModalItem={viewModalItem}
        handleIngredientChange={handleIngredientChange}
        removeIngredientRow={removeIngredientRow}
        addIngredientRow={addIngredientRow}
      />

      {/* Edit Item model */}
      <EditItemModal
        editModalOpen={editModalOpen}
        editItem={editItem}
        setEditModalOpen={setEditModalOpen}
        editForm={editForm}
        handleEditChange={handleEditChange}
        handleImageChange={handleImageChange}
        handleUpdateItem={handleUpdateItem}
      />

      {/* Barcode Modal */}
      <ItemViewModal
        viewModalItem={viewModalItem}
        setViewModalItem={setViewModalItem}
        viewModalTab={viewModalTab}
        setViewModalTab={setViewModalTab}
        editModalOpen={editModalOpen}
        setEditModalOpen={setEditModalOpen}
        fetchRacks={fetchRacks}
        getSubCategories={getSubCategories}
        setSubCategories={setSubCategories}
        rackList={rackList}
        editableDetails={editableDetails}
        handleDetailChange={handleDetailChange}
        previewImage={previewImage}
        handleCatalogImageChange={handleCatalogImageChange}
        handleSaveCatalog={handleSaveCatalog}
        availability={availability}
        handleAvailabilityChange={handleAvailabilityChange}
        handleSaveAvailability={handleSaveAvailability}
        selectedPrinter={selectedPrinter}
        setSelectedPrinter={setSelectedPrinter}
        printerList={printerList}
        handleSavePrinter={handleSavePrinter}
        handleAddPrinter={handleAddPrinter}
        getRecipeIngredients={getRecipeIngredients}
        setIngredients={setIngredients}
        setIngredientsModalOpen={setIngredientsModalOpen}
        subCategories={subCategories}
      />

      {/* view details modal */}
      <InventoryViewModal
        detailModalOpen={detailModalOpen}
        setDetailModalOpen={setDetailModalOpen}
        detailItem={detailItem}
        getDishLabel={getDishLabel}
      />
    </div>
  );
}
